import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	SRT_Row,
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type {
	OptimizationGroupModel,
	OptimizationModel,
} from '@/shared/generated'
import type { OnChangeFn } from '@tanstack/react-table'
import type { TFunction } from 'i18next'

import React, { useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns/fp'
import { all, prop, reduce } from 'lodash/fp'
import { ListFilterIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getOptimizationsMap,
	isEditableStatus,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { getWarnings, warningsCellRenderer } from '@/entities/pricing-campaign'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import FiltersPopover from '@/shared/components/filter/FiltersPopover'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { ValueType } from '@/shared/components/query-builder'
import SearchField from '@/shared/components/SearchField'
import { Button } from '@/shared/components/ui/button'
import { getMasterTableLocale } from '@/shared/i18n'
import { filterComparator } from '@/shared/tools/filtering'

type RowData = {
	optimization?: OptimizationModel
	group: OptimizationGroupModel
	warnings: string[]
}
export const SelectOptimizationGroups = ({
	selected,
	setSelected,
}: {
	selected: string[]
	setSelected: (selected: string[]) => void
}) => {
	const { t } = useTranslation()

	const [searchText, setSearchText] = useState('')
	const [rules, setRules] = useState<FilterSchema[]>([])
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const optimizations = useOptimizationsQuery({
		refetchOnMount: 'always',
		select: getOptimizationsMap,
	})
	const rows: RowData[] = (optimizationGroupsQuery?.data ?? [])
		.filter((group) =>
			searchText.length > 0
				? group.name
						.toLocaleLowerCase()
						.includes(searchText.toLocaleLowerCase())
				: true
		)
		?.map((group): RowData => {
			const optimization = optimizations?.data?.[group?.id]

			return {
				group,
				optimization,
				warnings: getWarnings(optimization),
			}
		})
		.filter(prop('group'))
		.filter((item) => all((filter) => filterComparator(filter, item), rules))

	return (
		<>
			<div className='flex items-center justify-between' id='modal-toolbar'>
				<div className='flex items-center space-x-2'>
					<SearchField
						onValueChange={setSearchText}
						value={searchText}
						placeholder={t('general_search')}
					/>
					<FiltersPopover
						enableReferencing={false}
						fields={[
							{
								value: 'group.name',
								label: 'Name',
								valueType: ValueType.string,
								enum: [],
							},
							{
								value: 'warnings',
								label: 'Warnings',
								valueType: ValueType.string,
								enum: [],
							},
							{
								value: 'optimization.total_products',
								label: 'Items',
								valueType: ValueType.number,
								enum: [],
							},
						]}
						filters={rules ?? []}
						dataChoices={{
							warnings: [
								'optimization_is_locked',
								'optimization_should_be_recreated',
								'optimization_is_not_editable',
							].map((value) => ({
								value,
								label: t(value, { defaultValue: value }),
							})),
						}}
						onApply={setRules}
					>
						<Button>
							<ListFilterIcon />
							{t('general_filter')}&nbsp;
							{rules.length ? `(${rules.length})` : ''}
						</Button>
					</FiltersPopover>
				</div>
			</div>
			<div className='border-base flex h-96 flex-col overflow-hidden rounded-lg border border-solid text-sm'>
				<TableView data={rows} selected={selected} setSelected={setSelected} />
			</div>
		</>
	)
}

function TableView({
	data,
	setSelected,
	selected,
}: {
	data: RowData[]
	setSelected(items: string[]): void
	selected: string[]
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SRT_SortingState>([])
	const rowSelection = useMemo(
		() =>
			reduce(
				(acc, id: string) => ({ ...acc, [id]: true }),
				{} as SRT_RowSelectionState
			)(selected),
		[selected]
	)
	const setRowSelection = useCallback(
		((updater: (old: SRT_RowSelectionState) => SRT_RowSelectionState) =>
			setSelected(
				Object.entries(updater(rowSelection))
					.filter(([, flag]) => flag)
					.map(([id]) => id)
			)) as OnChangeFn<SRT_RowSelectionState>,
		[rowSelection, setSelected]
	)
	const columns = useMemo(() => getColumns(t), [])
	const enableRowSelection = (row: SRT_Row<RowData>) =>
		Boolean(
			!row.original.warnings?.length &&
			row.original.optimization &&
			isEditableStatus(row.original.optimization.status)
		)
	const table = useMasterReactTable<RowData>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			sorting,
			rowSelection,
		},

		enableRowSelection: (row) =>
			Boolean(
				!row.original.warnings?.length &&
				row.original.optimization &&
				isEditableStatus(row.original.optimization.status)
			),
		defaultColumn: {
			minSize: 30,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		layoutMode: 'grid',
		enableColumnActions: false,
		enableVirtualization: true,
		divideX: false,
		getRowId: (row) => row.group.id,
		onSortingChange: setSorting,
		onRowSelectionChange: setRowSelection,
		getRowClassName: ({ row }) =>
			enableRowSelection(row) ? 'h-8' : 'h-8  cursor-not-allowed',
	})

	return <MasterTable table={table} />
}
const columnHelper = createSRTColumnHelper<RowData>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('group.name', {
		id: 'group.name',
		header: 'Name',
		Cell: defaultCell,
	}),
	columnHelper.accessor((row) => row.warnings?.length, {
		id: 'warnings',
		header: 'Warnings',
		Cell: ({ row, cellValue }) => {
			const { t } = useTranslation()

			return warningsCellRenderer(String(cellValue), row.original, t)
		},
		size: 100,
	}),
	columnHelper.accessor('optimization.total_products', {
		id: 'optimization.total_products',
		header: 'Items',
		Cell: ({ cellValue, row }) => (
			<DisabledCell disabled={Boolean(row.original.warnings?.length)}>
				<NumberField value={cellValue} />
			</DisabledCell>
		),
		size: 100,
	}),
	columnHelper.accessor('optimization.last_updated_date', {
		id: 'optimization.last_updated_date',
		header: t('general_last_updated'),
		Cell: ({ cellValue, row }) => {
			const value = cellValue

			return (
				<DisabledCell disabled={Boolean(row.original.warnings?.length)}>
					{value && format('MM/dd/yyyy hh:mm', new Date(value))}
				</DisabledCell>
			)
		},
		size: 210,
	}),
]

const DisabledCell = ({
	disabled,
	children,
}: {
	disabled: boolean
	children: React.ReactNode
}) => <span className={disabled ? 'text-disabled' : ''}>{children}</span>

const defaultCell = ({ cellValue, row }: { cellValue: any; row: any }) => (
	<DisabledCell disabled={Boolean(row.original.warnings?.length)}>
		{cellValue}
	</DisabledCell>
)
